import { Select, InputNumber, Switch, Alert } from 'antd';
import { Duration } from 'luxon';
import React, { useState, useEffect } from 'react';
import { IDatability } from '@mono/types/src/weather-widgets';

import { useDatasetById } from '../../utils/hooks/useDatasetById';

const MIN_INTERPOLATION_MINUTES = 30;

interface InterpolationIntervalFormGroupProps {
    datability: IDatability;
    disabled: boolean;
    onChange(value?: string): void;
    label?: string;
}

const InterpolationIntervalFormGroup: React.FC<InterpolationIntervalFormGroupProps> = ({ datability, onChange, label = 'Interpolation', disabled }) => {
    const [warning, setWarning] = useState<string | undefined>();
    const [enabled, setEnabled] = useState(false);
    const [numValue, setNumValue] = useState<number | undefined>(1);
    const [unit, setUnit] = useState<'D' | 'H' | 'M'>('H');
    const [isDisabled, setIsDisabled] = useState(true);
    const [dataset] = useDatasetById({ datasetId: datability.datasetId });

    useEffect(() => {
        if (disabled || !dataset || !datability.capabilityId) {
            return setIsDisabled(true);
        }
        const capabilityTimeInterpolation = dataset.capability_time_interpolations?.find(({ capability_id }) => capability_id === datability.capabilityId);
        const supportsInterpolation = !capabilityTimeInterpolation?.supports_time_interpolation;
        setIsDisabled(supportsInterpolation);
    }, [isDisabled, dataset, datability.capabilityId, disabled]);

    const duration = datability.interpolationInterval!;
    useEffect(() => {
        const durationLuxon = Duration.fromISO(duration);
        const { minutes = 0, hours = 0, days = 0 } = durationLuxon.toObject();

        if (minutes > 0) {
            setNumValue(minutes);
            setUnit('M');
            setEnabled(true);
        } else if (hours > 0) {
            setNumValue(hours);
            setUnit('H');
            setEnabled(true);
        } else if (days > 0) {
            setNumValue(days);
            setUnit('D');
            setEnabled(true);
        }
    }, [duration]);

    useEffect(() => {
        if (enabled === false) {
            onChange(undefined);
        }
    }, [enabled]);

    useEffect(() => {
        if (enabled) {
            const newDuration = unit === 'D' ? `P${numValue}${unit}T0H` : `PT${numValue}${unit}`;
            const newDurationLuxon = Duration.fromISO(newDuration);

            if (!newDurationLuxon.isValid) {
                setWarning(`Duration is invalid`);
            } else if (newDurationLuxon.as('minutes') < MIN_INTERPOLATION_MINUTES) {
                setWarning(`Interpolation interval cannot be shorther than ${MIN_INTERPOLATION_MINUTES} minutes`);
            } else {
                setWarning(undefined);
                onChange(newDuration);
            }
        }
    }, [numValue, unit, enabled]);

    return isDisabled ? (
        <></>
    ) : (
        <>
            <div style={{ gridColumn: '1/1' }}>
                <strong>{label}:</strong>
            </div>
            <div style={{ gridColumn: '2/2' }}>
                <Switch checked={enabled} onChange={setEnabled}></Switch>
            </div>
            {enabled && (
                <>
                    <div style={{ gridColumn: '2/4' }}>
                        <InputNumber style={{ width: '100%' }} min={1} value={numValue} onChange={(a: number) => setNumValue(a)} />
                    </div>
                    <div style={{ gridColumn: '4/6' }}>
                        <Select style={{ width: '100%' }} value={unit} onChange={setUnit}>
                            <Select.Option value={'D'}>Days</Select.Option>
                            <Select.Option value={'H'}>Hours</Select.Option>
                            <Select.Option value={'M'}>Minutes</Select.Option>
                        </Select>
                    </div>
                    {warning && (
                        <div style={{ gridColumn: '2/6' }}>
                            <Alert message={warning} type='error' />
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default InterpolationIntervalFormGroup;
